import React from 'react';

// ============================================================================
// Types
// ============================================================================

export interface AIModel {
  id: number;
  modelId: string;
  name: string;
  provider: string;
  contextWindow: number | null;
  inputPrice: number | null;
  outputPrice: number | null;
}

export interface AdminAIModelsPageProps {
  models: AIModel[];
  notice: string | null;

  // Sorting
  sortColumn: string;
  sortDirection: 'asc' | 'desc';

  // URLs
  dashboardPath: string;
  chatsPath: string;
  modelsPath: string;
  refreshPath: string;
  modelPath: (id: number) => string;
  newChatPath: (modelId: string) => string;
  csrfToken: string;

  // Labels
  labels: {
    title: string;
    dashboardLabel: string;
    chatsLabel: string;
    modelsLabel: string;
    refreshModelsButton: string;
    // Table
    providerColumn: string;
    modelColumn: string;
    contextWindowColumn: string;
    priceColumn: string;
    actionsColumn: string;
    viewButton: string;
    chatButton: string;
  };
}

// ============================================================================
// Helper Components
// ============================================================================

function AINavigation({
  activeTab,
  dashboardPath,
  chatsPath,
  modelsPath,
  labels,
}: {
  activeTab: 'dashboard' | 'chats' | 'models';
  dashboardPath: string;
  chatsPath: string;
  modelsPath: string;
  labels: AdminAIModelsPageProps['labels'];
}) {
  return (
    <nav className="mb-4">
      <ul className="nav nav-tabs">
        <li className="nav-item">
          <a href={dashboardPath} className={`nav-link ${activeTab === 'dashboard' ? 'active' : ''}`}>
            <i className="bi bi-speedometer2 me-1" />
            {labels.dashboardLabel}
          </a>
        </li>
        <li className="nav-item">
          <a href={chatsPath} className={`nav-link ${activeTab === 'chats' ? 'active' : ''}`}>
            <i className="bi bi-chat-dots me-1" />
            {labels.chatsLabel}
          </a>
        </li>
        <li className="nav-item">
          <a href={modelsPath} className={`nav-link ${activeTab === 'models' ? 'active' : ''}`}>
            <i className="bi bi-cpu me-1" />
            {labels.modelsLabel}
          </a>
        </li>
      </ul>
    </nav>
  );
}

function SortableColumn({
  column,
  currentColumn,
  currentDirection,
  basePath,
  children,
}: {
  column: string;
  currentColumn: string;
  currentDirection: 'asc' | 'desc';
  basePath: string;
  children: React.ReactNode;
}) {
  const isActive = currentColumn === column;
  const nextDirection = isActive && currentDirection === 'asc' ? 'desc' : 'asc';
  const href = `${basePath}?sort=${column}&direction=${nextDirection}`;

  return (
    <a href={href} className="text-decoration-none text-dark">
      {children}
      {isActive && (
        <i className={`bi bi-caret-${currentDirection === 'asc' ? 'up' : 'down'}-fill ms-1`} />
      )}
    </a>
  );
}

function formatNumber(num: number | null): string {
  if (num === null || num === undefined) return '';
  return num.toLocaleString();
}

function formatPrice(price: number | null): string {
  if (price === null || price === undefined) return '';
  return `$${price.toFixed(2)}`;
}

// ============================================================================
// Main Component
// ============================================================================

export function AdminAIModelsPage({
  models,
  notice,
  sortColumn,
  sortDirection,
  dashboardPath,
  chatsPath,
  modelsPath,
  refreshPath,
  modelPath,
  newChatPath,
  csrfToken,
  labels,
}: AdminAIModelsPageProps) {
  const handleRefresh = () => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = refreshPath;

    const csrfInput = document.createElement('input');
    csrfInput.type = 'hidden';
    csrfInput.name = 'authenticity_token';
    csrfInput.value = csrfToken;
    form.appendChild(csrfInput);

    document.body.appendChild(form);
    form.submit();
  };

  return (
    <div>
      {notice && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {notice}
          <button
            type="button"
            className="btn-close"
            onClick={(e) => {
              (e.target as HTMLButtonElement).closest('.alert')?.remove();
            }}
          />
        </div>
      )}

      <AINavigation
        activeTab="models"
        dashboardPath={dashboardPath}
        chatsPath={chatsPath}
        modelsPath={modelsPath}
        labels={labels}
      />

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h2">
          <i className="bi bi-cpu me-2" />
          {labels.title}
        </h1>
        <div>
          <button type="button" className="btn btn-primary me-2" onClick={handleRefresh}>
            <i className="bi bi-arrow-clockwise me-1" />
            {labels.refreshModelsButton}
          </button>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th className="ps-4">
                    <SortableColumn
                      column="provider"
                      currentColumn={sortColumn}
                      currentDirection={sortDirection}
                      basePath={modelsPath}
                    >
                      {labels.providerColumn}
                    </SortableColumn>
                  </th>
                  <th>
                    <SortableColumn
                      column="name"
                      currentColumn={sortColumn}
                      currentDirection={sortDirection}
                      basePath={modelsPath}
                    >
                      {labels.modelColumn}
                    </SortableColumn>
                  </th>
                  <th>
                    <SortableColumn
                      column="context_window"
                      currentColumn={sortColumn}
                      currentDirection={sortDirection}
                      basePath={modelsPath}
                    >
                      {labels.contextWindowColumn}
                    </SortableColumn>
                  </th>
                  <th>
                    <SortableColumn
                      column="price"
                      currentColumn={sortColumn}
                      currentDirection={sortDirection}
                      basePath={modelsPath}
                    >
                      {labels.priceColumn}
                    </SortableColumn>
                  </th>
                  <th className="text-end pe-4" colSpan={2}>
                    {labels.actionsColumn}
                  </th>
                </tr>
              </thead>
              <tbody>
                {models.map((model) => (
                  <tr key={model.id}>
                    <td className="ps-4">
                      <span className="badge bg-primary">{model.provider}</span>
                    </td>
                    <td className="fw-semibold">{model.name}</td>
                    <td>
                      {model.contextWindow && (
                        <span className="text-muted">{formatNumber(model.contextWindow)}</span>
                      )}
                    </td>
                    <td>
                      {model.inputPrice !== null && model.outputPrice !== null && (
                        <span className="text-muted">
                          {formatPrice(model.inputPrice)} / {formatPrice(model.outputPrice)}
                        </span>
                      )}
                    </td>
                    <td className="text-end pe-2">
                      <a href={modelPath(model.id)} className="btn btn-sm btn-outline-primary">
                        <i className="bi bi-eye" />
                      </a>
                    </td>
                    <td className="pe-4">
                      <a href={newChatPath(model.modelId)} className="btn btn-sm btn-success">
                        <i className="bi bi-chat-dots me-1" />
                        {labels.chatButton}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminAIModelsPage;
